import React from 'react';
import { pushViewerEvent } from '../../utils/viewerDataLayer';

function AccessoryList({
    loading,
    accessoriesLoading,
    availableAccessories,
    selectedAccessories,
    cart,
    isUpdatingAccessories,
    addToCart,
    removeFromCart,
    onAddAccessory,
    onAddAccessoryToViewerOnly,
    onRemoveAccessory,
    onRemoveAccessoryFromViewerOnly,
    onShowAccessoryDetails,
    categoryIdForCart,
    isRacingCategory = false,
    racingCategoryId = null
}) {
    if (loading || accessoriesLoading) {
        return (
            <div className="accessories-skeleton">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="accessory-skeleton-card">
                        <div className="skeleton-thumbnail"></div>
                        <div className="skeleton-details">
                            <div className="skeleton-line skeleton-title"></div>
                            <div className="skeleton-line skeleton-code"></div>
                            <div className="skeleton-line skeleton-price"></div>
                            <div className="skeleton-button"></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (availableAccessories.length === 0) {
        return (
            <div className="accessories-placeholder">
                <p>
                    <span language="ar">لا توجد ملحقات متاحة</span>
                    <span language="en">No accessories available</span>
                </p>
            </div>
        );
    }

    return (
        <>
            {availableAccessories.map((accessory, index) => {
                const isSelected = accessory.one3d_item_code && selectedAccessories.includes(accessory.one3d_item_code);
                const inCart = cart && cart[accessory.id];
                const isRacing = racingCategoryId && Array.isArray(accessory.category_ids) && accessory.category_ids.some((id) => String(id) === String(racingCategoryId));
                const showRacingIcon = isRacing || isRacingCategory;
                const handleCardClick = () => {
                    if (!accessory.has_3d || !accessory.one3d_item_code || isUpdatingAccessories) return;
                    // Card click (and 3D icon click via bubbling) should only affect viewer, not cart.
                    if (isSelected && onRemoveAccessoryFromViewerOnly) {
                        onRemoveAccessoryFromViewerOnly(accessory.one3d_item_code);
                    } else if (!isSelected && onAddAccessoryToViewerOnly) {
                        onAddAccessoryToViewerOnly(accessory);
                    }
                };

                return (
                    <div
                        key={`${accessory.id}-${index}`}
                        className={`accessory-card ${isSelected ? 'selected' : ''}${inCart ? ' accessory-card--in-cart' : ''}`}
                        onClick={handleCardClick}
                        role={accessory.has_3d && accessory.one3d_item_code ? 'button' : undefined}
                        tabIndex={accessory.has_3d && accessory.one3d_item_code ? 0 : undefined}
                        onKeyDown={(e) => {
                            if (accessory.has_3d && accessory.one3d_item_code && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                handleCardClick();
                            }
                        }}
                        style={(accessory.has_3d && accessory.one3d_item_code) ? { cursor: 'pointer' } : undefined}
                    >
                        <div className="accessory-icons">
                            {accessory.has_3d && accessory.one3d_item_code && (
                                <span
                                    className={`icon-3d icon-3d--static ${isSelected ? 'icon-3d-selected' : ''}`}
                                    title="3D"
                                    aria-hidden
                                    style={{ pointerEvents: 'none', cursor: 'default' }}
                                >
                                    <img src="/images/3d-icon-128x.png" alt="3D" style={{ width: '24px', height: '24px' }} />
                                </span>
                            )}
                            <span
                                className="icon-info"
                                title="Info"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    pushViewerEvent('viewer_info_icon_clicked', { accessory_id: accessory.id });
                                    onShowAccessoryDetails(accessory);
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <img src="/images/3d-info-icon-256x.png" alt="Info" style={{ width: '24px', height: '24px' }} />
                            </span>
                        </div>
                        <div className="accessory-thumbnail">
                            <img
                                src={accessory.thumbnail || '/images/default.jpg'}
                                alt={accessory.name}
                                onError={(e) => {
                                    e.target.src = '/images/default.jpg';
                                }}
                            />
                        </div>
                        <div className="accessory-details">
                            <div>
                                {showRacingIcon && (
                                    <img className="racing-logo-inline" src="/images/racing-horizontal.png" alt="Ford Racing" />
                                )}
                                <h3 className="accessory-name">{accessory.name}</h3>
                                <p className="accessory-code">{accessory.part_number}</p>
                                {((accessory.totalWithTax ?? accessory.price) != null) && (
                                    <p className="accessory-price">
                                        <span className="accessory-price-label">
                                            <span language="ar">السعر الإجمالي شامل التركيب</span>
                                            <span language="en">Total Price including Installation</span>
                                            *:
                                        </span>
                                        {' '}
                                        <span className="accessory-price-value">
                                            {new Intl.NumberFormat('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            }).format(Number(accessory.totalWithTax ?? accessory.price))} {accessory.currency}
                                        </span>
                                    </p>
                                )}
                            </div>
                            {accessory.has_3d && accessory.one3d_item_code ? (
                                inCart ? (
                                    <button
                                        type="button"
                                        className="btn-remove"
                                        disabled={isUpdatingAccessories}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            pushViewerEvent('viewer_remove_from_cart_clicked', { accessory_id: accessory.id });
                                            // On 3D canvas: remove from scene + cart; cart-only: cart only
                                            if (accessory.has_3d && accessory.one3d_item_code && isSelected && onRemoveAccessory) {
                                                onRemoveAccessory(accessory.one3d_item_code);
                                            } else if (removeFromCart && typeof removeFromCart === 'function') {
                                                removeFromCart(accessory.id);
                                            }
                                        }}
                                    >
                                        <span language="ar">إزالة</span>
                                        <span language="en">Remove</span>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn-add"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            pushViewerEvent('viewer_add_to_cart_clicked', {
                                                accessory_id: accessory.id,
                                                ui_source: 'card_in_viewer',
                                            });
                                            // Add to cart + 3D when not on canvas; if already on canvas, cart only
                                            if (isSelected) {
                                                if (addToCart && typeof addToCart === 'function') {
                                                    addToCart({
                                                        id: accessory.id,
                                                        quantity: 1,
                                                        category_id: categoryIdForCart || localStorage.getItem('category_id') || ''
                                                    }, 1);
                                                }
                                            } else if (onAddAccessory) {
                                                onAddAccessory(accessory);
                                            } else if (addToCart && typeof addToCart === 'function') {
                                                addToCart({
                                                    id: accessory.id,
                                                    quantity: 1,
                                                    category_id: categoryIdForCart || localStorage.getItem('category_id') || ''
                                                }, 1);
                                            }
                                        }}
                                        disabled={isUpdatingAccessories}
                                    >
                                        <span language="ar">الإضافة إلى عربة التسوق</span>
                                        <span language="en">Add to Cart</span>
                                    </button>
                                )
                            ) : (
                                inCart ? (
                                    <button
                                        type="button"
                                        className="btn-remove"
                                        disabled={isUpdatingAccessories}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            pushViewerEvent('viewer_remove_from_cart_clicked', { accessory_id: accessory.id });
                                            if (removeFromCart && typeof removeFromCart === 'function') {
                                                removeFromCart(accessory.id);
                                            }
                                        }}
                                    >
                                        <span language="ar">إزالة</span>
                                        <span language="en">Remove</span>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn-add"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            pushViewerEvent('viewer_add_to_cart_clicked', {
                                                accessory_id: accessory.id,
                                                ui_source: 'card_in_viewer',
                                            });
                                            if (addToCart && typeof addToCart === 'function') {
                                                addToCart({
                                                    id: accessory.id,
                                                    quantity: 1,
                                                    category_id: categoryIdForCart || localStorage.getItem('category_id') || ''
                                                }, 1);
                                            }
                                        }}
                                        disabled={isUpdatingAccessories}
                                    >
                                        <span language="ar">الإضافة إلى عربة التسوق</span>
                                        <span language="en">Add to Cart</span>
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                );
            })}
        </>
    );
}

export default AccessoryList;
